<template>
    <div :id="componentId"  :class="containerClasses" @click.stop="onSelectComponent"
         @mouseover.stop="isMouseOver = true" @mouseout.stop="isMouseOver = false"
         @dragover="onDragOver($event)" @dragleave="onDragLeave" @drop.stop="onDrop($event)">

        <div v-if="component.children.length === 0" class="bg-white w-full h-full text-slate-400 px-1 py-3 text-center">
            container
        </div>

        <component v-else v-for="(childComponent, index) in component.children"
                   :key="`${childComponent.type}-Layer${layer}-Index${index}`"
                   :is="childComponent.type" :component="initComponent(childComponent)" :index="index"
                   :parent-component="component" :editor-state="editorState" :layer="nextLayer"></component>
    </div>
</template>

<script>
import ComponentManager from "../utils/ComponentManager";
import webase from "../app/model";
import BackgroundFactory from "../factories/BackgroundFactory";
import webaseConstants from "../constants";
import { v4 as uuidv4 } from 'uuid';
import {animate} from "motion";

export default {
    name: 'FlexContainer',
    props: ['component', 'editorState', 'parentComponent', 'layer', 'index'],
    computed: {
        nextLayer() {
            return this.layer + 1
        },
        selectedClasses() {
            if (this.isDragOver) {
                return ['border-dashed', 'border-2', 'border-purple-500']
            }

            if (this.editorState.selectedComponentDefinition === this.component || this.isMouseOver) {
                return this.model.highlightClasses
            }

            return []
        },
        containerClasses() {
            if (this.component.children.length === 0) {
                return ['min-container-height', 'flex-1', 'w-full'].concat(this.selectedClasses)
            }

            let classes = this.component.classes.concat(this.selectedClasses)
            return classes
        }
    },
    watch: {
        'editorState.selectedComponentDefinition'(newComponentDefinition, oldComponentDefinition) {
            if (newComponentDefinition === this.component) {
                this.onSelectComponent()
                // console.log('Setting focus')
                // const flexContainer = this.$refs.flexContainer
                // flexContainer.focus()
                // console.log('Focus set')
            }
        },
        'editorState.theme'(newTheme, oldTheme) {
            if (newTheme === null) {
                this.removeExistingColorClasses()
                return
            }

            this.applyTheme()
        },
        'component.animation.type'(newAnimationType, oldAnimationType) {
            if(newAnimationType && newAnimationType !== 'none') {
                console.log('Flex Container, ' + newAnimationType)
                let animation = webase.utils.animation.getAnimation(newAnimationType)

                if(animation) {
                    animate(`#${this.componentId}`, animation.keyframes, animation.options)
                }
            }
        },
        'component.theme'(newBgTheme, oldBgTheme) {
            if (!newBgTheme) {
                this.removeExistingColorClasses()
            } else if (newBgTheme !== oldBgTheme) {
                this.removeExistingColorClasses()
                this.applyBackground(newBgTheme)
            }
        }
    },
    data() {
        return {
            componentId: `id${uuidv4()}`,
            model: webase.model,
            manager: new ComponentManager(this.component),
            isMouseOver: false,
            isDragOver: false
        }
    },
    created() {

    },
    mounted() {
        //console.log('FlexContainer.mounted, key: ' + this.$.vnode.key + ', layer: ' + this.layer )
        this.component.location = this.parentComponent ? `${this.parentComponent.location}.${this.layer}` : `${this.layer}`
        // this.component.parent = this.parentComponent

        // commenting out for now... feels aggressive to do this on mount every time
        // if(this.editorState.theme) {
        //     this.applyTheme()
        // }

        if(this.component.animation === undefined) {
            this.component.animation = webase.utils.animation.initAnimationProperty()
        }
    },
    methods: {

        onDragOver(event) {
            event.preventDefault()
            this.isDragOver = true
        },
        onDragLeave() {
            this.isDragOver = false
        },
        onDrop(event) {
            const componentString = event.dataTransfer.getData("text/plain");

            let newComponent = JSON.parse(componentString)
            console.log('Flex on drop')
            console.log(newComponent)
            let componentLayout = JSON.parse(JSON.stringify(newComponent.layout, this.model.layoutProperties))
            this.component.children.push(componentLayout)
            webase.eventDispatcher.emit('onComponentAdded')
            this.selectDroppedComponent(componentLayout)
            this.isDragOver = false
        },
        // @keyup.delete="onDeleteKeyPress()" tabindex="10"
        // TODO: need a better solution for deleting components with the delete key that does not
        // interfere with the editing of contenteditable elements like Headers and Labels
        // One idea is to look at the currently selected component and make sure it isn't contenteditable

        // onDeleteKeyPress() {
        //     console.log('Delete key press in flex container')
        //     webase.eventDispatcher.emit('deleteRequested')
        // },
        initComponent(childComponent) {
            childComponent.parent = this.component
            return childComponent
        },
        onSelectComponent() {
            this.editorState.selectedComponent = this
            this.editorState.selectedComponentDefinition = this.component
            this.editorState.selectedComponentManager = this.manager
        },
        applyTheme() {
            // console.log('Applying theme on FlexContainer, key: ' + this.$.vnode.key + ', layer: ' + this.layer )
            if (this.layer === 0) {
                this.applyRootClasses()
            } else if (this.layer === 1) {
                this.applyBackgroundClasses()
                // } else if(this.hasBgSet() ) {
                //     this.applyPrimaryBackgroundClasses()
            } else if (this.component.theme) {
                this.removeExistingColorClasses()
                this.applyBackground(this.component.theme)
            }
        },
        applyBackgroundClasses() {
            this.removeExistingColorClasses()

            if (this.index % 2 === 0) {
                this.applyBackground('primary')
            } else {
                this.applyBackground('secondary')
            }
        },
        applyPrimaryBackgroundClasses() {
            this.removeExistingColorClasses()
            this.applyBackground('primary')
        },
        applyBackground(backgroundName) {
            if (!this.editorState.theme) {
                this.component.theme = ''
                return
            }

            let backgroundFactory = new BackgroundFactory()
            let background = this.editorState.theme.metadata.backgrounds[backgroundName]
            this.component.classes = this.component.classes.concat(backgroundFactory.cssClasses(background))
            this.component.theme = backgroundName
        },
        hasBgSet() {
            let bgClass = webase.utils.style.findClassWithPrefix(this.component.classes, 'bg-')
            return bgClass !== null
        },
        applyRootClasses() {
            // NOTE... this will clash with text- color classes ... NEED TO FIX to be more specific
            this.component.classes = webase.utils.array.filter(this.component.classes, 'text-')

            let textClass = this.getTextClass()

            if (textClass && !webase.utils.style.isClassPresent(this.component.classes, textClass)) {
                this.component.classes.push(textClass)
            }
        },
        removeExistingColorClasses() {
            let classPrefixes = webaseConstants.bgColorClasses
            this.component.classes = webase.utils.array.filterAll(this.component.classes, classPrefixes)
        },
        // removeExistingClass(classPrefix) {
        //     let previousTextClass = webase.utils.style.findClassWithPrefix(this.component.classes, classPrefix)
        //
        //     if (previousTextClass) {
        //         this.component.classes = webase.utils.array.filter(this.component.classes, previousTextClass)
        //     }
        // },
        getTextClass() {
            if (this.editorState.theme) {
                return 'text-' + this.editorState.theme.metadata.fontSizes.body
            }

            return null
        },
        selectDroppedComponent(newComponent) {
            setTimeout(() => {
                this.editorState.selectedComponentDefinition = newComponent
            }, 700)
        }
    }
}
</script>
